import React, { useState, useEffect } from 'react';
import { AdminUser, CreateUserRequest, UpdateUserRequest } from '../../types/Auth';
import { authApi } from '../../services/authApi';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<AdminUser>({} as AdminUser);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newUserData, setNewUserData] = useState<CreateUserRequest>({
    username: '',
    email: '',
    password: '',
  });
  const [changingPasswordUser, setChangingPasswordUser] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await authApi.getAllUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Feil ved henting av brukere');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditStart = (user: AdminUser) => {
    setEditingUser(user.userID);
    setEditFormData({ ...user });
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditFormData({} as AdminUser);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    
    try {
      const updateData: UpdateUserRequest = {
        username: editFormData.username,
        email: editFormData.email
      };
      
      await authApi.updateUser(editingUser, updateData);
      setEditingUser(null);
      setEditFormData({} as AdminUser);
      await fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Feil ved oppdatering av bruker');
    }
  };

  const handleFieldChange = (field: keyof AdminUser, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNewUserStart = () => {
    setIsCreatingNew(true);
    setEditingUser(null);
  };

  const handleNewUserCancel = () => {
    setIsCreatingNew(false);
    setNewUserData({
      username: '',
      email: '',
      password: '',
    });
  };

  const handleNewUserSave = async () => {
    try {
      await authApi.createUser(newUserData);
      setIsCreatingNew(false);
      setNewUserData({
        username: '',
        email: '',
        password: '',
      });
      await fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Feil ved opprettelse av ny bruker');
    }
  };

  const handleNewUserFieldChange = (field: keyof CreateUserRequest, value: string) => {
    setNewUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChangeStart = (userId: number) => {
    setChangingPasswordUser(userId);
    setNewPassword('');
    setEditingUser(null); // Cancel any other editing
  };

  const handlePasswordChangeCancel = () => {
    setChangingPasswordUser(null);
    setNewPassword('');
  };

  const handlePasswordChangeSave = async () => {
    if (!changingPasswordUser || !newPassword.trim()) return;
    
    try {
      await authApi.updateUserPassword(changingPasswordUser, { newPassword });
      setChangingPasswordUser(null);
      setNewPassword('');
      // No need to refresh users list as password isn't displayed
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Feil ved oppdatering av passord');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Er du sikker på at du vil slette denne brukeren?')) {
      try {
        await authApi.deleteUser(id);
        await fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        setError('Feil ved sletting av bruker');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nb-NO');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Laster brukere...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Noe gikk galt</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {/* Table Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Brukere ({users.length})</h2>
        <button
          onClick={handleNewUserStart}
          disabled={isCreatingNew}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ny Bruker
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 w-full">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Brukernavn
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                E-post
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Passord
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Opprettet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sist Endret
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* New User Row */}
            {isCreatingNew && (
              <tr className="bg-green-50 border-2 border-green-200">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="text-green-600 font-medium">NY</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="text"
                    value={newUserData.username}
                    onChange={(e) => handleNewUserFieldChange('username', e.target.value)}
                    placeholder="Brukernavn"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => handleNewUserFieldChange('email', e.target.value)}
                    placeholder="E-post"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => handleNewUserFieldChange('password', e.target.value)}
                    placeholder="Passord"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  -
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  -
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleNewUserSave}
                      className="text-green-600 hover:text-green-800 transition-colors"
                      title="Lagre ny bruker"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNewUserCancel}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                      title="Avbryt"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            )}
            
            {users.map((user) => {
              const isEditing = editingUser === user.userID;
              
              return (
                <tr key={user.userID} className={`transition-colors ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.userID}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.username || ''}
                        onChange={(e) => handleFieldChange('username', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="email"
                        value={editFormData.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{user.email}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {changingPasswordUser === user.userID ? (
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nytt passord"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    ) : (
                      <button
                        onClick={() => handlePasswordChangeStart(user.userID)}
                        className="text-orange-600 hover:text-orange-800 text-sm transition-colors"
                        title="Endre passord"
                      >
                        Endre passord
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.modifiedDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      {changingPasswordUser === user.userID ? (
                        <>
                          <button
                            onClick={handlePasswordChangeSave}
                            className="text-orange-600 hover:text-orange-800 transition-colors"
                            title="Lagre nytt passord"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handlePasswordChangeCancel}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title="Avbryt passord-endring"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : isEditing ? (
                        <>
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Lagre"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title="Avbryt"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditStart(user)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Rediger"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(user.userID)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Slett"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {users.length === 0 && (
        <div className="text-center py-12">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-gray-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen brukere funnet</h3>
          <p className="text-gray-500">Klikk på "Ny Bruker" for å legge til din første bruker.</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;